import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Building2, ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';

const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Politique de Confidentialite | copropriete.app</title>
        <meta name="description" content="Politique de confidentialite et protection des donnees personnelles - copropriete.app - Conforme RGPD" />
      </Helmet>

      <header className="bg-white border-b border-slate-200 py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold flex items-center gap-2 text-slate-900">
            <Building2 className="w-6 h-6 text-indigo-600" />
            copropriete.app
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-4">Politique de Confidentialite</h1>
        <p className="text-slate-600 mb-8">Protection de vos donnees personnelles conformement au RGPD</p>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-3">
            <Shield className="w-8 h-8 text-indigo-600" />
            <div>
              <div className="font-semibold text-slate-900">Conforme RGPD</div>
              <div className="text-sm text-slate-500">Reglement europeen</div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-3">
            <Lock className="w-8 h-8 text-emerald-600" />
            <div>
              <div className="font-semibold text-slate-900">Donnees chiffrees</div>
              <div className="text-sm text-slate-500">SSL/TLS 256 bits</div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-3">
            <Database className="w-8 h-8 text-blue-600" />
            <div>
              <div className="font-semibold text-slate-900">Hebergement UE</div>
              <div className="text-sm text-slate-500">Serveurs europeens</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8 text-slate-700 leading-relaxed">
          
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-600" />
              Introduction
            </h2>
            <p>La presente politique de confidentialite a pour objet d'informer les utilisateurs du site copropriete.app sur la maniere dont leurs donnees personnelles sont collectees et traitees par SAS GMP Developpement.</p>
            <p className="mt-2">SAS GMP Developpement s'engage a respecter la reglementation en vigueur applicable au traitement de donnees a caractere personnel et, en particulier, le Reglement (UE) 2016/679 du Parlement europeen et du Conseil du 27 avril 2016 (ci-apres "RGPD") ainsi que la loi n 78-17 du 6 janvier 1978 relative a l'informatique, aux fichiers et aux libertes, modifiee.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-indigo-600" />
              Article 1 - Responsable du traitement
            </h2>
            <p>Le responsable du traitement des donnees a caractere personnel est :</p>
            <div className="bg-slate-50 p-4 rounded-lg mt-2">
              <p><strong>SAS GMP Developpement</strong></p>
              <p>Siege social : 51 Rue Michel Jandet, 73390 Chamoux-sur-Gelon, France</p>
              <p>SIRET : 980 507 784 00018</p>
              <p>Email : <a href="mailto:gmpdeveloppement@gmail.com" className="text-indigo-600 hover:underline">gmpdeveloppement@gmail.com</a></p>
              <p>Representant : Gary Paillet</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Article 2 - Donnees collectees</h2>
            <p>Dans le cadre de l'utilisation du Service, SAS GMP Developpement est amenee a collecter les categories de donnees suivantes :</p>
            
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.1 Donnees d'identification</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Nom et prenom</li>
              <li>Adresse email</li>
              <li>Numero de telephone (facultatif)</li>
              <li>Adresse postale (pour les coproprietaires)</li>
              <li>Raison sociale (pour les professionnels)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.2 Donnees relatives a la copropriete</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Adresse de la copropriete</li>
              <li>Numero de lot et tantiemes</li>
              <li>Role au sein de la copropriete (president, secretaire, tresorier, coproprietaire)</li>
              <li>Documents uploades (proces-verbaux, convocations, factures, etc.)</li>
              <li>Informations financieres (montants des appels de fonds, paiements)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.3 Donnees de connexion</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Adresse IP</li>
              <li>Type de navigateur et version</li>
              <li>Systeme d'exploitation</li>
              <li>Date et heure de connexion</li>
              <li>Pages visitees</li>
            </ul>
            
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.4 Donnees de paiement</h3>
            <p>Les donnees de paiement (numero de carte bancaire) sont collectees et traitees exclusivement par notre prestataire de paiement Stripe, certifie PCI-DSS. SAS GMP Developpement n'a pas acces aux donnees bancaires completes.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Article 3 - Finalites du traitement</h2>
            <p>Les donnees personnelles collectees ont pour finalites :</p>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 font-semibold">Finalite</th>
                    <th className="text-left p-3 font-semibold">Base legale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="p-3">Fourniture et gestion du Service</td>
                    <td className="p-3">Execution du contrat</td>
                  </tr>
                  <tr>
                    <td className="p-3">Gestion des comptes utilisateurs</td>
                    <td className="p-3">Execution du contrat</td>
                  </tr>
                  <tr>
                    <td className="p-3">Gestion de la facturation et des paiements</td>
                    <td className="p-3">Execution du contrat / Obligation legale</td>
                  </tr>
                  <tr>
                    <td className="p-3">Support client et assistance technique</td>
                    <td className="p-3">Interet legitime</td>
                  </tr>
                  <tr>
                    <td className="p-3">Amelioration du Service</td>
                    <td className="p-3">Interet legitime</td>
                  </tr>
                  <tr>
                    <td className="p-3">Envoi de communications commerciales</td>
                    <td className="p-3">Consentement</td>
                  </tr>
                  <tr>
                    <td className="p-3">Prevention de la fraude</td>
                    <td className="p-3">Interet legitime</td>
                  </tr>
                  <tr>
                    <td className="p-3">Respect des obligations legales</td>
                    <td className="p-3">Obligation legale</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Article 4 - Duree de conservation</h2>
            <p>Les donnees personnelles sont conservees pendant la duree strictement necessaire aux finalites pour lesquelles elles ont ete collectees :</p>
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 font-semibold">Type de donnees</th>
                    <th className="text-left p-3 font-semibold">Duree de conservation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="p-3">Donnees du compte utilisateur</td>
                    <td className="p-3">Duree de la relation contractuelle + 3 ans</td>
                  </tr>
                  <tr>
                    <td className="p-3">Documents de copropriete</td>
                    <td className="p-3">10 ans (obligation legale copropriete)</td>
                  </tr>
                  <tr>
                    <td className="p-3">Donnees de facturation</td>
                    <td className="p-3">10 ans (obligation comptable)</td>
                  </tr>
                  <tr>
                    <td className="p-3">Logs de connexion</td>
                    <td className="p-3">1 an</td>
                  </tr>
                  <tr>
                    <td className="p-3">Cookies</td>
                    <td className="p-3">13 mois maximum</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">A l'expiration de ces delais, les donnees sont supprimees ou anonymisees.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Article 5 - Destinataires des donnees</h2>
            <p>Les donnees personnelles peuvent etre communiquees aux destinataires suivants :</p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li><strong>Personnel habilite de SAS GMP Developpement</strong> : equipe technique et support client</li>
              <li><strong>Autres membres de la copropriete</strong> : selon les permissions configurees (ex : le tresorier voit les informations de paiement)</li>
              <li><strong>Sous-traitants</strong> :
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Stripe (paiement)</li>
                  <li>Hebergeur cloud (stockage des donnees)</li>
                  <li>Service d'envoi d'emails transactionnels</li>
                </ul>
              </li>
              <li><strong>Autorites competentes</strong> : en cas d'obligation legale ou de requisition judiciaire</li>
            </ul>
            <p className="mt-4">SAS GMP Developpement ne vend ni ne loue vos donnees personnelles a des tiers a des fins commerciales.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Article 6 - Transfert de donnees hors UE</h2>
            <p>Les donnees personnelles sont hebergees au sein de l'Union Europeenne.</p>
            <p className="mt-2">Dans le cas ou certains sous-traitants seraient situes en dehors de l'UE (notamment Stripe), SAS GMP Developpement s'assure que des garanties appropriees sont mises en place conformement a l'article 46 du RGPD (clauses contractuelles types, certification, etc.).</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Article 7 - Securite des donnees</h2>
            <p>SAS GMP Developpement met en oeuvre des mesures techniques et organisationnelles appropriees pour proteger les donnees personnelles contre la destruction accidentelle ou illicite, la perte, l'alteration, la divulgation ou l'acces non autorises :</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Chiffrement des communications (SSL/TLS)</li>
              <li>Chiffrement des donnees sensibles au repos</li>
              <li>Controle d'acces strict base sur les roles</li>
              <li>Authentification securisee avec hachage des mots de passe</li>
              <li>Sauvegardes regulieres et testees</li>
              <li>Journalisation des acces</li>
              <li>Tests de securite reguliers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Article 8 - Vos droits</h2>
            <p>Conformement au RGPD, vous disposez des droits suivants sur vos donnees personnelles :</p>
            
            <div className="space-y-4 mt-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-900">Droit d'acces</h4>
                <p className="text-sm mt-1">Obtenir la confirmation que des donnees vous concernant sont traitees et en recevoir une copie.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-900">Droit de rectification</h4>
                <p className="text-sm mt-1">Faire corriger des donnees inexactes ou completer des donnees incompletes.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-900">Droit a l'effacement</h4>
                <p className="text-sm mt-1">Demander la suppression de vos donnees dans les cas prevus par le RGPD.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-900">Droit a la limitation</h4>
                <p className="text-sm mt-1">Demander la suspension temporaire du traitement de vos donnees.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-900">Droit a la portabilite</h4>
                <p className="text-sm mt-1">Recevoir vos donnees dans un format structure et lisible par machine.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-900">Droit d'opposition</h4>
                <p className="text-sm mt-1">Vous opposer au traitement de vos donnees pour des raisons tenant a votre situation particuliere.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-semibold text-slate-900">Droit de retirer votre consentement</h4>
                <p className="text-sm mt-1">Retirer a tout moment le consentement que vous avez donne pour un traitement specifique.</p>
              </div>
            </div>
            
            <p className="mt-4">Pour exercer vos droits, vous pouvez :</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Utiliser les fonctionnalites disponibles dans votre espace personnel</li>
              <li>Contacter notre DPO a l'adresse : <a href="mailto:gmpdeveloppement@gmail.com" className="text-indigo-600 hover:underline">gmpdeveloppement@gmail.com</a></li>
              <li>Ecrire a : SAS GMP Developpement - Chamoux-sur-Gelon, 73390, France</li>
            </ul>
            <p className="mt-4">SAS GMP Developpement s'engage a repondre a votre demande dans un delai d'un (1) mois a compter de sa reception. Ce delai peut etre prolonge de deux (2) mois supplementaires en cas de demande complexe.</p>
            <p className="mt-2">Vous disposez egalement du droit d'introduire une reclamation aupres de la CNIL (www.cnil.fr) si vous estimez que le traitement de vos donnees ne respecte pas la reglementation.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Article 9 - Cookies</h2>
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">9.1 Qu'est-ce qu'un cookie ?</h3>
            <p>Un cookie est un petit fichier texte depose sur votre terminal (ordinateur, tablette, smartphone) lors de la visite d'un site web. Il permet de stocker des informations relatives a votre navigation.</p>
            
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">9.2 Cookies utilises</h3>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 font-semibold">Nom</th>
                    <th className="text-left p-3 font-semibold">Finalite</th>
                    <th className="text-left p-3 font-semibold">Duree</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="p-3">auth_token</td>
                    <td className="p-3">Authentification de l'utilisateur</td>
                    <td className="p-3">Session / 30 jours</td>
                  </tr>
                  <tr>
                    <td className="p-3">copro_selected</td>
                    <td className="p-3">Memorisation de la copropriete selectionnee</td>
                    <td className="p-3">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">9.3 Gestion des cookies</h3>
            <p>Les cookies strictement necessaires au fonctionnement du Service ne peuvent pas etre refuses. Pour les autres cookies, vous pouvez a tout moment modifier vos preferences via les parametres de votre navigateur.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Article 10 - Modification de la politique</h2>
            <p>SAS GMP Developpement se reserve le droit de modifier la presente politique de confidentialite a tout moment. En cas de modification substantielle, les utilisateurs seront informes par email et/ou par notification sur le Service.</p>
            <p className="mt-2">La date de derniere mise a jour est indiquee en bas de ce document.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Article 11 - Contact
            </h2>
            <p>Pour toute question relative a la presente politique de confidentialite ou pour exercer vos droits, vous pouvez contacter notre responsable :</p>
            <div className="bg-indigo-50 p-4 rounded-lg mt-4 border border-indigo-100">
              <p className="font-semibold text-indigo-900">Responsable Protection des Donnees</p>
              <p className="text-indigo-700">SAS GMP Developpement</p>
              <p className="text-indigo-700">51 Rue Michel Jandet, 73390 Chamoux-sur-Gelon, France</p>
              <p className="text-indigo-700">Email : <a href="mailto:gmpdeveloppement@gmail.com" className="underline">gmpdeveloppement@gmail.com</a></p>
            </div>
          </section>

          <div className="pt-6 border-t border-slate-200 text-sm text-slate-500">
            <p>Derniere mise a jour : Mars 2026</p>
          </div>
        </div>
      </main>

      <footer className="bg-slate-900 text-slate-400 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm">2026 SAS GMP Developpement - copropriete.app</p>
        </div>
      </footer>
    </div>
  );
};

export default PolitiqueConfidentialite;
